import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Badge, Button, Card, Col, Form, Nav, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { JsonObjectExpression } from 'typescript';
import moment from 'moment';
import _ from 'lodash';
import {
  CREATE_GIFT,
  CREATE_MESSAGE_TEMPLATE,
  DELETE_MESSAGE_TEMPLATE,
  EDIT_MESSAGE_TEMPLATE,
  GET_DRAFT_GIFTS,
  GET_MESSAGE_TEMPLATES,
  GET_NUMBER_OF_RECIPIENTS,
  RETRY_GIFT,
  UPDATE_GIFT,
} from './graphql';
import {
  CreateGiftAdminMutation,
  CreateGiftAdminMutationVariables,
  CreateMessageTemplateMutation,
  CreateMessageTemplateMutationVariables,
  DeleteMessageTemplateMutation,
  DeleteMessageTemplateMutationVariables,
  DraftGiftFragmentFragment,
  EditMessageTemplateMutation,
  EditMessageTemplateMutationVariables,
  GetAllMessageTemplatesQuery,
  GetAllMessageTemplatesQueryVariables,
  GetDashboardDataQueryVariables,
  GetDraftGiftsQuery,
  GetNumberOfRecipientsQuery,
  GetNumberOfRecipientsQueryVariables,
  GiftFiltersInput,
  ItemAvailability,
  LevelRewardInput,
  LevelRewardInputType,
  MessageTemplate,
  UpdateGiftAdminMutation,
  UpdateGiftAdminMutationVariables,
} from '../../../../../__gqltypes__';
import { MassGiftFilters } from './MassGiftFilters';
import EditRewardsModal from '../../../liveops/components/EditRewardsModal';
import Chip from '../../../liveops/components/Chip';
import CostDisplay from '../../../../../utils/dashboards/components/widgets/CostDisplay';
import { LoadingLogo } from '../../components/Modal';

const DEFAULT_MASS_GIFT_APP_VERSION = '003.020.000';
const DEFAULT_MASS_GIFT_DAYS_SINCE_LAST_ONLINE = 30;
const MAX_MASS_GIFT_DAYS_SINCE_LAST_ONLINE = 90;

const DEFAULT_MASS_GIFT_FILTERS: GiftFiltersInput = {
  appVersion: DEFAULT_MASS_GIFT_APP_VERSION,
  daysSinceLastOnline: DEFAULT_MASS_GIFT_DAYS_SINCE_LAST_ONLINE,
};

type CurrentGift = {
  id?: string;
  message: MessageTemplate | null;
  filters: GiftFiltersInput;
  rewards: LevelRewardInput[];
  sendDate: string;
  messageId?: string | null;
  recipientsCount?: number | null;
  sentRecipientsCount?: number | null;
  deliveryStatus?: string | null;
  totalBatches?: number | null;
  pendingBatches?: number | null;
  runningBatches?: number | null;
  completedBatches?: number | null;
  failedBatches?: number | null;
};

type RetryGiftAdminMutation = {
  retryGiftAdmin: DraftGiftFragmentFragment;
};

type RetryGiftAdminMutationVariables = {
  id: string;
};

const getMassGiftStatusVariant = (
  deliveryStatus?: string | null
): 'danger' | 'secondary' | 'success' | 'warning' => {
  switch (deliveryStatus) {
    case 'COMPLETED':
      return 'success';
    case 'SENDING':
      return 'warning';
    case 'PARTIAL_FAILED':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getMassGiftStatusContent = (deliveryStatus?: string | null): React.ReactNode => {
  switch (deliveryStatus) {
    case 'COMPLETED':
      return <i className="fas fa-check" aria-label="COMPLETED" />;
    case 'SENDING':
      return <i className="fas fa-paper-plane" aria-label="SENDING" />;
    case 'PARTIAL_FAILED':
      return <i className="fas fa-exclamation-triangle" aria-label="PARTIAL_FAILED" />;
    case 'PENDING':
    default:
      return <i className="fas fa-clock" aria-label={deliveryStatus ?? 'PENDING'} />;
  }
};

const getVisibleMassGiftFilters = (filters: GiftFiltersInput = {}): GiftFiltersInput => {
  const visibleFilters = { ...filters };

  if (_.isNil(visibleFilters.appVersion) || visibleFilters.appVersion < DEFAULT_MASS_GIFT_APP_VERSION) {
    visibleFilters.appVersion = DEFAULT_MASS_GIFT_APP_VERSION;
  }

  if (_.isNil(visibleFilters.daysSinceLastOnline) || visibleFilters.daysSinceLastOnline < 0) {
    visibleFilters.daysSinceLastOnline = DEFAULT_MASS_GIFT_DAYS_SINCE_LAST_ONLINE;
  } else if (visibleFilters.daysSinceLastOnline > MAX_MASS_GIFT_DAYS_SINCE_LAST_ONLINE) {
    visibleFilters.daysSinceLastOnline = MAX_MASS_GIFT_DAYS_SINCE_LAST_ONLINE;
  }

  return visibleFilters;
};

const getCurrentGiftFromDraft = (
  gift: DraftGiftFragmentFragment | null | undefined,
  availableMsgTemplates: MessageTemplate[]
): CurrentGift => ({
  id: gift?.id,
  message: availableMsgTemplates.find((template) => template.id === gift?.messageId) ?? null,
  filters: getVisibleMassGiftFilters(gift?.filters),
  rewards:
    gift?.rewards.map(({ amount, itemId, type }) => ({
      amount,
      itemId,
      type: type as LevelRewardInputType,
    })) ?? [],
  sendDate: gift?.sendDate ? moment(gift.sendDate).format('YYYY-MM-DDTHH:mm') : '',
  messageId: gift?.messageId,
  recipientsCount: gift?.recipientsCount,
  sentRecipientsCount: gift?.sentRecipientsCount,
  deliveryStatus: gift?.deliveryStatus,
  totalBatches: gift?.totalBatches,
  pendingBatches: gift?.pendingBatches,
  runningBatches: gift?.runningBatches,
  completedBatches: gift?.completedBatches,
  failedBatches: gift?.failedBatches,
});

const SectionHeader = ({
  title,
  tooltipId,
  tooltip,
}: {
  title: string;
  tooltipId: string;
  tooltip: string;
}): JSX.Element => (
  <Card.Header className="d-flex align-items-center">
    <span className="font-weight-bold mr-2">{title}</span>
    <OverlayTrigger placement="right" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
      <i className="fas fa-info-circle text-muted" />
    </OverlayTrigger>
  </Card.Header>
);

const MassGift = () => {
  const [currentGift, setCurrentGift] = useState<CurrentGift>({
    message: null,
    filters: DEFAULT_MASS_GIFT_FILTERS,
    rewards: [],
    sendDate: '',
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [showEditGift, setShowEditGift] = useState(false);
  const [selectedGift, setSelectedGift] = useState<LevelRewardInput | null>(null);
  const [giftIndex, setGiftIndex] = useState(0);

  const [numberOfRecipients, setNumberOfRecipients] = useState(0);

  const [availableMsgTemplates, setAvailableMsgTemplates] = useState<MessageTemplate[]>([]);
  const [newMsgTemplateInEdit, setNewMsgTemplateInEdit] = useState<string>('');
  const [msgTemplateEditOn, setMsgTemplateEditOn] = useState(false);

  const [metadataList, setMetadataList] = useState<string[]>([]);

  const {
    data: draftGiftsData,
    loading: giftsLoading,
    refetch,
  } = useQuery<GetDraftGiftsQuery, GetDashboardDataQueryVariables>(GET_DRAFT_GIFTS);

  const draftGifts = draftGiftsData?.getDraftGifts ?? [];

  const { data: msgTemplates, loading: msgTemplatesLoading } = useQuery<
    GetAllMessageTemplatesQuery,
    GetAllMessageTemplatesQueryVariables
  >(GET_MESSAGE_TEMPLATES);

  const [createMsgTemplate, { loading: createMTLoading }] = useMutation<
    CreateMessageTemplateMutation,
    CreateMessageTemplateMutationVariables
  >(CREATE_MESSAGE_TEMPLATE);

  const [editMsgTemplate, { loading: editMTLoading }] = useMutation<
    EditMessageTemplateMutation,
    EditMessageTemplateMutationVariables
  >(EDIT_MESSAGE_TEMPLATE);

  const [deleteMsgTemplate, { loading: deleteMTLoading }] = useMutation<
    DeleteMessageTemplateMutation,
    DeleteMessageTemplateMutationVariables
  >(DELETE_MESSAGE_TEMPLATE);

  const [getNumberOfRecipients, { loading: numberOfRecipLoading }] = useLazyQuery<
    GetNumberOfRecipientsQuery,
    GetNumberOfRecipientsQueryVariables
  >(GET_NUMBER_OF_RECIPIENTS);

  const [createGift, { loading: sendGiftsLoading }] = useMutation<
    CreateGiftAdminMutation,
    CreateGiftAdminMutationVariables
  >(CREATE_GIFT);

  const [updateGift] = useMutation<UpdateGiftAdminMutation, UpdateGiftAdminMutationVariables>(UPDATE_GIFT);
  const [retryGift, { loading: retryGiftLoading }] = useMutation<
    RetryGiftAdminMutation,
    RetryGiftAdminMutationVariables
  >(RETRY_GIFT);
  const isMassGiftFiltersInvalid =
    !_.isNil(currentGift.filters?.daysSinceLastOnline) &&
    (currentGift.filters.daysSinceLastOnline < 0 ||
      currentGift.filters.daysSinceLastOnline > MAX_MASS_GIFT_DAYS_SINCE_LAST_ONLINE);

  useEffect(() => {
    if (!msgTemplatesLoading && msgTemplates) {
      setAvailableMsgTemplates(msgTemplates.getMessageTemplates);
    }
  }, [msgTemplates, msgTemplatesLoading]);

  const metadata: Record<string, JsonObjectExpression> = useMemo(() => {
    if (metadataList.length === 0) {
      return {};
    }
    const m: Record<string, JsonObjectExpression> = {};
    metadataList.forEach((val, index) => {
      if (val) {
        m[index.toString()] = JSON.parse(val);
      }
    });
    return m;
  }, [metadataList]);

  const onRefreshNumber = () => {
    const filters = getVisibleMassGiftFilters(currentGift.filters);
    setCurrentGift({ ...currentGift, filters });
    getNumberOfRecipients({
      variables: {
        filters: _.omit(filters, '__typename'),
      },
    }).then((value) => {
      const data_ = value.data?.getPotentialNumberOfRecipients;
      setNumberOfRecipients(data_?.count ?? 0);
      setMetadataList(data_?.metadataList ?? []);
    });
  };

  const onCreateGifts = () => {
    const filters = getVisibleMassGiftFilters(currentGift.filters);
    setCurrentGift({ ...currentGift, filters });
    if (currentGift.id) {
      updateGift({
        variables: {
          id: currentGift.id,
          gifts: currentGift.rewards.map((r: LevelRewardInput) => _.omit(r, '__typename')),
          filters: _.omit(filters, '__typename'),
          messageId: currentGift?.message?.id ?? null,
          sendDate: moment(currentGift.sendDate).toDate(),
          recipientsCount: numberOfRecipients,
        },
      })
        .then((value) => {
          const data_ = value.data?.updateGiftAdmin;
          setCurrentGift(getCurrentGiftFromDraft(data_, availableMsgTemplates));
          setNumberOfRecipients(data_?.recipientsCount ?? 0);
          refetch();
        })
        .catch((e) => {
          console.log(e);
        });
    } else {
      createGift({
        variables: {
          gifts: currentGift.rewards,
          filters,
          messageId: currentGift?.message?.id ?? null,
          sendDate: moment(currentGift.sendDate).toDate(),
          recipientsCount: numberOfRecipients,
        },
      })
        .then((value) => {
          const data_ = value.data?.createGiftAdmin;
          setCurrentGift(getCurrentGiftFromDraft(data_, availableMsgTemplates));
          setNumberOfRecipients(data_?.recipientsCount ?? 0);
          refetch();
        })
        .catch((e) => {
          console.log(e);
        });
    }
  };

  const onChange = useCallback((newFilters: GiftFiltersInput, newSendDate: string | null) => {
    setCurrentGift((prev) => ({
      ...prev,
      filters: _.isEmpty(newFilters) ? DEFAULT_MASS_GIFT_FILTERS : newFilters,
      sendDate: newSendDate ?? '',
    }));
  }, []);

  const handleAddGift = useCallback(
    (newReward: LevelRewardInput) => {
      if (isEditMode) {
        const newGifts = [...currentGift.rewards];
        newGifts[giftIndex] = {
          type: newReward.type,
          amount: newReward.amount,
          itemId: newReward.itemId ?? '',
        };
        setCurrentGift((prev) => ({ ...prev, rewards: newGifts }));
      } else {
        const newGifts = [...currentGift.rewards];
        newGifts.push({
          type: newReward.type,
          amount: newReward.amount,
          itemId: newReward.itemId ?? '',
        });
        setCurrentGift((prev) => ({ ...prev, rewards: newGifts }));
      }
      setShowEditGift(false);
    },
    [isEditMode, currentGift.rewards, giftIndex]
  );

  const handleRemoveGift = useCallback(
    (idx: number) => {
      if (idx >= 0 && idx < currentGift.rewards.length) {
        let newGifts = [...currentGift.rewards];
        newGifts = newGifts.filter((val, index) => index !== idx);
        setCurrentGift((prev) => ({ ...prev, rewards: newGifts }));
      }
    },
    [currentGift.rewards]
  );

  const handleShowEditGift = useCallback(
    (idx: number, isEdit: boolean) => {
      setIsEditMode(isEdit);
      if (isEdit) {
        const rew = currentGift.rewards[idx];
        const levelReward: LevelRewardInput = {
          amount: rew.amount,
          type: rew.type,
          itemId: rew.itemId,
        };
        setSelectedGift(levelReward);
      } else {
        setSelectedGift(null);
      }
      setGiftIndex(idx);
      setShowEditGift(true);
    },
    [currentGift.rewards]
  );

  const handleChangeGiftMessage = useCallback(
    (id: string, inEditMode: boolean) => {
      if (id === 'None') {
        setCurrentGift((prev) => ({ ...prev, message: null }));
        setNewMsgTemplateInEdit('');
        return;
      }
      const newMsg = availableMsgTemplates.find((val) => val.id === id);
      if (newMsg) {
        setCurrentGift((prev) => ({ ...prev, message: newMsg }));
        if (inEditMode) {
          setNewMsgTemplateInEdit(newMsg.message);
        }
      }
    },
    [availableMsgTemplates]
  );

  const handleCreateNewMsgTemplate = () => {
    createMsgTemplate({
      variables: {
        msg: newMsgTemplateInEdit,
      },
    }).then((mutRes) => {
      setAvailableMsgTemplates(mutRes.data?.createMessageTemplate ?? []);
    });
  };

  const handleEditNewMsgTemplate = () => {
    if (currentGift.message) {
      editMsgTemplate({
        variables: {
          id: currentGift.message.id,
          msg: newMsgTemplateInEdit,
        },
      }).then((mutRes) => {
        const newTmps = mutRes.data?.editMessageTemplate ?? [];
        setAvailableMsgTemplates(newTmps);
        const newMessage = newTmps.find((value) => value.id === currentGift.message?.id);
        if (newMessage) {
          setCurrentGift((prev) => ({ ...prev, message: newMessage }));
        }
      });
    }
  };

  const handleDeleteNewMsgTemplate = useCallback(() => {
    if (currentGift.message) {
      deleteMsgTemplate({
        variables: {
          id: currentGift.message.id,
        },
      }).then((mutRes) => {
        setAvailableMsgTemplates(mutRes.data?.deleteMessageTemplate ?? []);
        setCurrentGift((prev) => ({ ...prev, message: null }));
        setNewMsgTemplateInEdit('');
      });
    }
  }, [currentGift.message, deleteMsgTemplate]);

  const addGift = () => {
    setCurrentGift({
      rewards: [],
      filters: DEFAULT_MASS_GIFT_FILTERS,
      message: null,
      sendDate: '',
    });
    setMetadataList([]);
    setNumberOfRecipients(0);
  };

  const setGift = (id: string) => {
    const data_ = draftGifts.find((g) => g.id === id);
    setCurrentGift(getCurrentGiftFromDraft(data_, availableMsgTemplates));
    setNumberOfRecipients(data_?.recipientsCount ?? 0);
  };

  const copyText = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  };

  const copyGiftId = (event: React.MouseEvent, giftId: string) => {
    event.preventDefault();
    event.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(giftId).catch(() => copyText(giftId));
      return;
    }
    copyText(giftId);
  };

  const handleRetryGift = useCallback(() => {
    if (!currentGift.id) {
      return;
    }

    retryGift({
      variables: {
        id: currentGift.id,
      },
    })
      .then((value) => {
        const data_ = value.data?.retryGiftAdmin;
        setCurrentGift(getCurrentGiftFromDraft(data_, availableMsgTemplates));
        setNumberOfRecipients(data_?.recipientsCount ?? 0);
        refetch();
      })
      .catch((e) => {
        console.log(e);
      });
  }, [availableMsgTemplates, currentGift.id, refetch, retryGift]);

  const mainLoading = numberOfRecipLoading || sendGiftsLoading || retryGiftLoading;
  const msgTemplateLoading = createMTLoading || editMTLoading || deleteMTLoading;
  return (
    <div className="d-flex">
      <div className="col-2">
        <Nav.Item className="mb-3">
          {giftsLoading ? (
            <Spinner animation="border" />
          ) : (
            <>
              <Button className="w-100" variant="success" onClick={addGift}>
                <i className="fas fa-plus mr-2" />
                Create gift
              </Button>
            </>
          )}
        </Nav.Item>
        <Nav variant="pills">
          {draftGifts
            .slice()
            .sort((a, b) => b.sendDate - a.sendDate)
            .map((gift) => (
              <Nav.Item onClick={() => setGift(gift.id)} key={gift.id} className="w-100 position-relative">
                <Nav.Link
                  active={currentGift.id === gift.id}
                  eventKey={gift.id}
                  className="d-flex justify-content-between text-break align-items-center"
                >
                  <span>
                    {moment(gift.sendDate).format('DD-MM-YYYY HH:mm')}{' '}
                    <small className="text-muted" style={{ cursor: 'text', fontSize: 10, userSelect: 'text' }}>
                      {gift.id}
                    </small>
                    <Button
                      className="p-0 ml-1 align-baseline text-muted"
                      onClick={(event) => copyGiftId(event, gift.id)}
                      size="sm"
                      title="Copy ID"
                      variant="link"
                    >
                      <i className="fas fa-copy" style={{ fontSize: 10 }} />
                    </Button>
                  </span>
                  <Badge
                    className="m-1"
                    pill
                    title={gift.deliveryStatus ?? 'PENDING'}
                    variant={getMassGiftStatusVariant(gift.deliveryStatus)}
                  >
                    {getMassGiftStatusContent(gift.deliveryStatus)}
                  </Badge>
                </Nav.Link>
              </Nav.Item>
            ))}
        </Nav>
      </div>
      <Card className="shadow mb-4 w-100">
        <EditRewardsModal
          isEdit={isEditMode}
          tier={null}
          show={showEditGift}
          level={0}
          reward={selectedGift}
          handleSave={handleAddGift}
          handleClose={() => setShowEditGift(false)}
          disableCurrencySpecialEvent
          disableSubscriptionDay={false}
          disableMonthlyBuff
          enableItemLevelEdit
          enableExtendedFragmentSelector
          liveOpsAvailabilities={[ItemAvailability.EARN]}
        />
        <MassGiftFilters
          filters={currentGift.filters}
          onChange={onChange}
          sendDate={currentGift.sendDate}
          onRefresh={onRefreshNumber}
          recipientPreview={
            <div className="border rounded p-2 bg-light">
              {mainLoading ? (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="mr-2" />
                  <span>Searching users...</span>
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center mb-2">
                    <span className="font-weight-bold mr-2">Recipient Preview</span>
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="mass-gift-recipient-preview-tooltip">
                          Shows the estimated recipient count and BigQuery cost metadata after Search User is clicked.
                        </Tooltip>
                      }
                    >
                      <i className="fas fa-info-circle text-muted mr-3" />
                    </OverlayTrigger>
                  </div>
                  <CostDisplay metadata={metadata} />
                  <div className="font-weight-bold mt-2">Number of recipients: {numberOfRecipients}</div>
                  {currentGift.id ? (
                    <div className="mt-1 text-muted small">
                      Delivery progress: {currentGift.sentRecipientsCount ?? 0}/{currentGift.recipientsCount ?? 0} (
                      {currentGift.deliveryStatus ?? 'PENDING'})
                      {currentGift.totalBatches ? (
                        <>
                          {' '}
                          Batches: {currentGift.completedBatches ?? 0}/{currentGift.totalBatches}
                          {currentGift.failedBatches ? `, failed: ${currentGift.failedBatches}` : ''}
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          }
        />

        <Card className="m-2 border border-secondary">
          <SectionHeader
            title="Gift Message"
            tooltipId="mass-gift-message-tooltip"
            tooltip="Choose an optional message template for the inbox gift banner, or create and edit reusable templates."
          />
          <Card.Body>
            <Form.Text muted className="mb-3">
              Message templates are optional; selecting None sends the gift without a custom template.
            </Form.Text>
            <Form.Group>
              {msgTemplateLoading ? (
                <LoadingLogo show={msgTemplateLoading} />
              ) : (
                <>
                  <Form.Row>
                    <Form.Label className="ml-1">Current Message:</Form.Label>
                    <Form.Label>{currentGift.message ? currentGift.message.message : ''}</Form.Label>
                  </Form.Row>

                  <Form.Label>Choose Message</Form.Label>
                  <Form.Control
                    as="select"
                    value={currentGift.message?.id ?? 'None'}
                    className="mb-4"
                    onChange={(e) => handleChangeGiftMessage(e.target.value, msgTemplateEditOn)}
                  >
                    <option key="None">None</option>
                    {availableMsgTemplates.map((msgTmp) => {
                      return (
                        <option key={msgTmp.id} value={msgTmp.id}>
                          {msgTmp.message}
                        </option>
                      );
                    })}
                  </Form.Control>

                  <Form.Row>
                    <Form.Label className="mr-2 ml-1">Enable Editing Message Template</Form.Label>
                    <Form.Check
                      type="checkbox"
                      checked={msgTemplateEditOn}
                      className="align-self-start"
                      onChange={(e) => {
                        if (!msgTemplateEditOn && currentGift.message) {
                          setNewMsgTemplateInEdit(currentGift.message.message);
                        } else if (msgTemplateEditOn) {
                          setNewMsgTemplateInEdit('');
                        }
                        setMsgTemplateEditOn((val) => !val);
                      }}
                    />
                  </Form.Row>

                  <Form.Label>Create New Message Template</Form.Label>

                  <Form.Control
                    as="textarea"
                    value={newMsgTemplateInEdit}
                    onChange={(e) => setNewMsgTemplateInEdit(e.target.value)}
                  />

                  <Row className="ml-1">
                    <Button
                      onClick={handleCreateNewMsgTemplate}
                      className="mt-4 mr-2"
                      disabled={newMsgTemplateInEdit.length === 0 || msgTemplateEditOn}
                    >
                      <i className="fas fa-plus mr-2" />
                      Create
                    </Button>

                    <Button
                      onClick={handleEditNewMsgTemplate}
                      className="mt-4 mr-2"
                      disabled={newMsgTemplateInEdit.length === 0 || !msgTemplateEditOn}
                    >
                      <i className="fas fa-edit mr-2" />
                      Edit
                    </Button>

                    <Button
                      onClick={handleDeleteNewMsgTemplate}
                      variant="danger"
                      className="mt-4"
                      disabled={!currentGift.message || !msgTemplateEditOn}
                    >
                      <i className="fas fa-trash mr-2" />
                      Delete
                    </Button>
                  </Row>
                </>
              )}
            </Form.Group>
          </Card.Body>
        </Card>

        <Card className="m-2 border border-secondary">
          <SectionHeader
            title="Gift Rewards"
            tooltipId="mass-gift-rewards-tooltip"
            tooltip="Add the reward payload that each matched recipient will receive when the mass gift is sent."
          />
          <Card.Body>
            <Form.Text muted className="mb-3">
              Add at least one reward and a send date before sending the gift.
            </Form.Text>
            <Row className="ml-1 mb-4 justify-content-start">
              <Button
                variant="success"
                className="mr-2"
                onClick={() => handleShowEditGift(currentGift.rewards.length, false)}
              >
                <i className="fas fa-plus mr-2" />
                Add Reward
              </Button>
              <Button
                onClick={onCreateGifts}
                disabled={
                  currentGift.rewards.length === 0 || currentGift.sendDate.length === 0 || isMassGiftFiltersInvalid
                }
              >
                <i className="fas fa-gift mr-2" />
                Send Gift
              </Button>
              {currentGift.id &&
              (currentGift.deliveryStatus === 'SENDING' || currentGift.deliveryStatus === 'PARTIAL_FAILED') ? (
                <Button className="ml-2" onClick={handleRetryGift} variant="outline-warning">
                  <i className="fas fa-redo mr-2" />
                  Retry
                </Button>
              ) : null}
            </Row>
            <Row className="mb-3">
              <Col className="d-flex flex-column align-items-center">
                <Form.Label className="font-weight-bold">Rewards</Form.Label>
                {currentGift.rewards.map((gift: LevelRewardInput, index: number) => {
                  return (
                    <Row key={index} className="mt-2 align-items-end">
                      <Chip
                        reward={gift}
                        canDelete
                        btnAction={() => handleRemoveGift(index)}
                        globalAction={() => handleShowEditGift(index, true)}
                      />
                    </Row>
                  );
                })}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Card>
    </div>
  );
};

export default MassGift;
